import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription} from "rxjs";
import {TabOptionModel} from "../../../../x-components/tab-selection/models/tab-option-model";
import {TimeSeriesPerformanceStoreService} from "../../store/time-series-performance-store.service";
import {CombinedOrderElementDto, CombinedOrderElementDtoKeys} from '../../models/core/combined-order-element-dto';
import {TimeseriesBarChartStore} from "../../store/timeseries-bar-chart/timeseries-bar-chart-store";
import {RevenueBreakdownStore} from "../../store/revenueBreakdown/revenue-breakdown-store";
import {FilterKeys} from "../../../common/utils/filter/enums/filter-keys";
import {RevenueCalculationStrategy} from "../../../common/enums/strategies/revenue-calculation-strategy";
import {FilterVO} from "../../../common/utils/filter/models/filter-vo";
import {TimeUtilities} from "../../../../../utils/time-utilities";
import {TimeRepository} from "../../../single-event-analysis/repositories/core/time-repository/time-repository";
import {LinkedDtoRepository} from "../../../common/repositories/linked-dto-repository";
import {ColorMapService} from "../../../common/colors/color-map.service";
import {OrderRepository} from "../../repositories/core/orders/order-repository";
import {CategoryRevenueRepository} from "../../repositories/derived/category-revenue/category-revenue-repository";
import {EventRepository} from "../../repositories/core/event/event-repository";
import {EmploymentCostRepository} from "../../repositories/core/employment-cost/employment-cost-repository";
import {HoverService} from "../../components/charts/revenue-doughnut-chart/services/hover.service";
import {
  PercentUiControllerService
} from "../../components/charts/revenue-doughnut-chart/services/percent-ui-controller.service";
import {EventOverviewTableComponent} from "../../components/tables/event-overview-table/event-overview-table.component";
import {TabSelectionComponent} from "../../../../x-components/tab-selection/tab-selection.component";
import {
  AnalyticsPercentUiCardComponent
} from "../../components/charts/revenue-doughnut-chart/components/analytics-percent-ui-card/analytics-percent-ui-card.component";
import {
  RevenueDoughnutChartComponent
} from "../../components/charts/revenue-doughnut-chart/revenue-doughnut-chart.component";
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {SwitchComponent} from "../../../../x-components/switch/switch.component";
import {
  RelativeTimeSeriesChartComponent
} from "../../components/charts/relative-time-series-chart/relative-time-series-chart.component";
import {
  RevenueTimeSeriesChartComponent
} from "../../components/charts/revenue-time-series-chart/revenue-time-series-chart.component";


@Component({
  selector: 'app-time-series-performance-tracker-view',
  templateUrl: './time-series-performance-tracker-view.component.html',
  styleUrls: ['./time-series-performance-tracker-view.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    EventOverviewTableComponent,
    TabSelectionComponent,
    AnalyticsPercentUiCardComponent,
    RevenueDoughnutChartComponent,
    SwitchComponent,
    RelativeTimeSeriesChartComponent,
    RevenueTimeSeriesChartComponent
  ],
  providers: [
    TimeSeriesPerformanceStoreService,
    TimeseriesBarChartStore,
    RevenueBreakdownStore,
    // Common
    LinkedDtoRepository,
    ColorMapService,
    // Stores
    TimeRepository,
    OrderRepository,
    CategoryRevenueRepository,
    EventRepository,
    EmploymentCostRepository,
    HoverService,
    PercentUiControllerService
  ]
})
export class TimeSeriesPerformanceTrackerViewComponent implements OnInit, OnDestroy {
  // Utilities
  subscription: Subscription = new Subscription();

  // General Filter Flags
  // Todo: check if better to use in store
  //isEmployeeSalesExcluded: boolean = false;

  // View Options/Toggles
  isTrendChartEnabled: boolean = false;
  statisticsOverviewOption: keyof CombinedOrderElementDto = "locationId"

  doughnutRevenueChartDisplayOptions: TabOptionModel[] = [
    new TabOptionModel(FilterKeys.BookingType, "Zahlungsart", true),
    new TabOptionModel(FilterKeys.LocationId, "Standort", false),
    new TabOptionModel(FilterKeys.CompositionTypeId, "Kategorie", false),
  ]


  // Chart References
  @ViewChild('multiEventRevenueChart', {static: false}) multiEventRevenueChart: any;
  @ViewChild('multiEventTrendChart', {static: false}) multiEventTrendChart: any;

  @ViewChild('doughnutRevenueChart', {static: false}) doughnutRevenueChart: any;
  @ViewChild('percentCard', {static: false}) percentCard: any;

  // Data Caches
  categoryFilter: {
    [id: string]: { filter: FilterVO<any>, isActive: boolean, category: keyof CombinedOrderElementDto }
  } = {};
  protected readonly TimeUtilities = TimeUtilities;
  protected readonly CombinedOrderElementDtoKeys = CombinedOrderElementDtoKeys;

  constructor(public timeSeriesPerformanceStore: TimeSeriesPerformanceStoreService,
              public timeSeriesBarChartStore: TimeseriesBarChartStore,
              public revenueBreakdownStore: RevenueBreakdownStore,
  ) {

  }

  // region Filter Group Management
  getStatisticsGroup(option: keyof CombinedOrderElementDto): {
    filter: FilterVO<any>,
    isActive: boolean,
    category: keyof CombinedOrderElementDto
  }[] {
    let data = this.timeSeriesPerformanceStore.getStatisticsOverviewOption(option)
    // append all items to the categoryFilter if not already present
    data.forEach((item) => {
      let id = this.getIdOfStatisticsGroup(option, item.id)
      if (!this.categoryFilter[id]) {
        this.categoryFilter[id] = {filter: this.createFilter(option, item.id), isActive: true, category: option}
      }
    })
    // filter where category is the option
    return Object.values(this.categoryFilter).filter((item) => item.category === option)
  }

  createFilter(option: keyof CombinedOrderElementDto, id: string) {
    if (option === "bookingType")
      return new FilterVO(FilterKeys.BookingType, parseInt(id))
    return new FilterVO(option as FilterKeys, id)
  }

  getIdOfStatisticsGroup(option: keyof CombinedOrderElementDto, id: string) {
    return id + option
  }

  getStatisticsGroupModel(option: keyof CombinedOrderElementDto, id: string) {
    return this.categoryFilter[this.getIdOfStatisticsGroup(option, id)]
  }

  setStatisticsOverviewOption(option: keyof CombinedOrderElementDto, id: string, isActive: boolean) {
    if (!isActive) {
      this.timeSeriesPerformanceStore.addFilter(this.categoryFilter[this.getIdOfStatisticsGroup(option, id)].filter)
    } else {
      this.timeSeriesPerformanceStore.removeFilter(this.categoryFilter[this.getIdOfStatisticsGroup(option, id)].filter)
    }

    this.getStatisticsGroupModel(option, id).isActive = isActive
  }

  // endregion

  // region Toggle General Filters

  // Todo add this to the store instead
  /*excludeEmployeeSales(isExcluded: boolean) {
    this.employeeSalesExcluded = isExcluded;
    // todo implement filter
    if(this.employeeSalesExcluded){
      this.timeSeriesPerformanceStore.addFilter(new FilterVO(FilterKeys.BookingType, 2))
    }
    else{
      this.timeSeriesPerformanceStore.removeFilter(new FilterVO(FilterKeys.BookingType, 2))
    }
  }*/

  // endregion

  // region Toggle Chart Display Options
  // Todo: move to store
  /*switchBarChartTrend(isTrendChart: boolean) {
    this.isBarChartTrendEnabled = isTrendChart;

    this.updateTrendChart()

  }*/

  // endregion

  getRevenue(id: string, option: keyof CombinedOrderElementDto) {
    // find in this.timeSeriesPerformanceStore.getStatisticsOverviewOption(option)
    let data = this.timeSeriesPerformanceStore.getStatisticsOverviewOption(option)
    let item = data.find((item) => item.id.toString() === id.toString())
    if (item) {
      return item.revenue
    }
    return 0
  }

  netRevenueToggle(event: boolean) {
    this.timeSeriesPerformanceStore.isNetRevenue = event;
    if (this.timeSeriesPerformanceStore.isNetRevenue) {
      this.timeSeriesPerformanceStore.revenueCalculationMethodOption = RevenueCalculationStrategy.revenueExcludingTaxes
    } else {
      this.timeSeriesPerformanceStore.revenueCalculationMethodOption = RevenueCalculationStrategy.revenue
    }
    this.timeSeriesPerformanceStore.update()
  }


  /*updateAll(){
    this.updateTrendChart()
    this.updateDoughnutRevenueChart()
  }*/

  /*updateTrendChart(){
    if(!this.isBarChartTrendEnabled)
    {
      this.multiEventRevenueChart.forceUpdate(this.timeSeriesPerformanceStore.barChartAbsoluteData);
      return
    }
    this.multiEventTrendChart.forceUpdate(this.timeSeriesPerformanceStore.barChartTrendData)
  }*/

  /*updateDoughnutRevenueChart(){
    switch (this.doughnutRevenueChartDisplayOption) {
      case DisplayOptions.PAYMENT_METHOD:
        this.doughnutRevenueChart.forceUpdate(this.timeSeriesPerformanceStore.pieChartBookingTypeData)
        break;
      case DisplayOptions.LOCATION:
        this.doughnutRevenueChart.forceUpdate(this.timeSeriesPerformanceStore.pieChartLocationData)
        break;
      case DisplayOptions.COMPOSITION_TYPE:
        this.doughnutRevenueChart.forceUpdate(this.timeSeriesPerformanceStore.pieChartCompositionTypeData)
        break;
    }
  }*/

  ngOnInit(): void {
    this.subscription.add(this.timeSeriesPerformanceStore.updateNotifier.subscribe(
      () => {
        //this.updateAll()
      }
    ))
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  setDoughnutRevenueChartDisplayOption(option: TabOptionModel) {
    this.revenueBreakdownStore.changeDisplayOption(option.id as FilterKeys)
  }

  switchBarChartTrend(isTrendChart: boolean) {
    this.timeSeriesBarChartStore.isBarChartTrendEnabled = isTrendChart;

  }

  // Todo create static utils functio
  isScreenSmallerThan(size: number): boolean {
    return window.innerWidth < size;
  }
}
